<article
  class="bg-white border border-solid border-neutral-20 rounded shadow-card relative"
>
  <button
    type="button"
    class="flex w-full text-left [font:inherit] [color:inherit] bg-transparent border-0 gap-3.5 items-start px-4 py-3.5 cursor-pointer"
    [attr.aria-expanded]="isExpanded()"
    (click)="toggle()"
  >
    <span
      class="flex-none w-9 h-9 rounded grid place-items-center"
      [class]="tileClass()"
    >
      <span
        class="fa-icons icon-regular icon-18"
        [class]="'icon-' + meta().icon"
      ></span>
    </span>

    <span class="flex-1 min-w-0 flex flex-col gap-0.5">
      <span class="flex items-center gap-2 flex-wrap">
        <span class="subtitle-1 text-neutral-90">{{ action().title }}</span>
        @if (edited()) {
          <span
            class="text-caption bg-neutral-10 text-neutral-90 rounded-full px-2"
          >
            {{ 'actionsInbox.card.edited' | translate }}
          </span>
        }
      </span>

      <span class="text-body-2 text-neutral-60">{{ action().summary }}</span>

      <span class="flex flex-wrap gap-x-3.5 gap-y-1.5 mt-1.5 items-center">
        @if (client(); as client) {
          <span
            class="text-caption text-neutral-90 inline-flex items-center gap-1.5"
          >
            <span class="avatar avatar-xs bg-inserv-10">{{
              client.name | nameInitials
            }}</span>
            {{ client.name }}
          </span>
        }
        <span
          class="text-caption text-neutral-60 inline-flex items-center gap-1.5"
        >
          <span
            class="fa-icons icon-solid icon-12"
            [class]="'icon-' + triggerIcon()"
          ></span>
          {{ 'actionsInbox.trigger.' + action().source.trigger | translate }} ·
          {{ action().source.label }} · {{ action().createdAt | relativeDate }}
        </span>
        <span
          class="text-caption text-neutral-60 bg-neutral-5 rounded px-1.5 font-mono"
        >
          {{ action().recipe }}
        </span>
      </span>
    </span>

    <span class="flex-none flex items-center gap-3">
      <span
        class="text-caption rounded-full px-2.5 py-0.5 font-medium whitespace-nowrap"
        [class]="tileClass()"
      >
        {{ meta().labelKey | translate }}
      </span>
      <span
        class="fa-icons icon-solid icon-14 text-neutral-60"
        [class]="isExpanded() ? 'icon-chevron-up' : 'icon-chevron-down'"
      ></span>
    </span>
  </button>

  @if (isExpanded()) {
    <div
      class="border-t border-solid border-neutral-20 pt-4 pb-4.5 pr-4 pl-[66px] flex flex-col gap-3"
    >
      @if (body(); as component) {
        @if (hasPayload()) {
          <ng-container
            *ngComponentOutlet="component; inputs: bodyInputs()"
          ></ng-container>
        } @else if (payloadError(); as error) {
          <div class="flex flex-col items-start gap-2" role="alert">
            <span class="text-body-2 text-error-100">
              {{ 'actionsInbox.card.loadFailed' | translate }}
            </span>
            <span class="text-caption text-neutral-60 break-words">
              {{ error }}
            </span>
            <button
              kendoButton
              themeColor="secondary"
              size="small"
              (click)="retry.emit(action().id)"
            >
              {{ 'actionsInbox.inbox.retry' | translate }}
            </button>
          </div>
        } @else {
          <span class="text-body-2 text-neutral-60">
            {{ 'actionsInbox.card.loading' | translate }}
          </span>
        }
      } @else {
        <span class="text-body-2 text-neutral-60">
          {{ 'actionsInbox.card.unsupportedType' | translate }}
        </span>
      }
    </div>

    <div
      class="border-t border-solid border-neutral-20 bg-max-white rounded-b pt-3 pb-3.5 pr-4 pl-[66px] flex justify-between items-center gap-2.5 flex-wrap"
    >
      @switch (footerMode()) {
        @case ('executing') {
          <span class="text-body-2 text-warning-140 font-medium">
            {{ 'actionsInbox.footer.working' | translate }}
          </span>
          <span class="text-caption text-neutral-60">
            {{ 'actionsInbox.footer.auditWritten' | translate }}
          </span>
        }

        @case ('settled') {
          @if (action().status === Status.Rejected) {
            <span class="text-body-2 text-error-100 font-medium">
              {{ 'actionsInbox.footer.rejected' | translate }}
            </span>
          } @else if (action().status === Status.Failed) {
            <span class="text-body-2 text-error-100 font-medium">
              {{
                action().failureReason ??
                  ('actionsInbox.footer.failed' | translate)
              }}
            </span>
          } @else {
            <span
              class="text-body-2 text-bush-green-140 font-medium flex items-center gap-1.5"
            >
              <span
                class="fa-icons icon-solid icon-circle-check icon-14"
              ></span>
              {{ action().result }}
            </span>
          }
          @if (action().decision; as decision) {
            <span class="text-caption text-neutral-60">
              {{
                'actionsInbox.footer.logged'
                  | translate
                    : { by: decision.by, at: decision.at | relativeDate }
              }}
            </span>
          }
        }

        @case ('rejecting') {
          <div class="flex gap-2 items-center flex-wrap w-full">
            <span class="text-body-2 text-neutral-60">
              {{ 'actionsInbox.footer.rejectReason' | translate }}
            </span>
            <div class="flex-1 min-w-[200px]">
              <kendo-textbox
                [value]="rejectReason()"
                (valueChange)="rejectReason.set($event ?? '')"
                [placeholder]="
                  'actionsInbox.footer.rejectReasonPlaceholder' | translate
                "
              ></kendo-textbox>
            </div>
            <button
              kendoButton
              themeColor="secondary"
              (click)="confirmReject()"
            >
              {{
                'actionsInbox.footer.confirmReject'
                  | translate: { verb: meta().declineKey | translate }
              }}
            </button>
            <button kendoButton fillMode="flat" (click)="cancelReject()">
              {{ 'actionsInbox.footer.cancel' | translate }}
            </button>
          </div>
        }

        @default {
          @if (action().failureReason; as refusal) {
            <span class="text-body-2 text-error-100 w-full" role="alert">
              {{ refusal }}
            </span>
          }
          <div class="flex gap-2 items-center flex-wrap">
            @if (explicitDecisions() && undecided() > 0) {
              <button kendoButton themeColor="secondary" (click)="acceptAll()">
                {{ 'actionsInbox.footer.acceptAll' | translate }}
              </button>
            }
          </div>
          <div class="flex gap-2 items-center flex-wrap">
            <button kendoButton fillMode="flat" (click)="startReject()">
              {{ meta().declineKey | translate }}
            </button>
            <button
              kendoButton
              themeColor="primary"
              [disabled]="approveDisabled()"
              (click)="onApprove()"
            >
              <span
                class="fa-icons icon-solid icon-14 mr-1.5"
                [class]="'icon-' + meta().verbIcon"
              ></span>
              {{ verbKey() | translate: { count: accepted() } }}
            </button>
          </div>
        }
      }
    </div>
  }
</article>
